import { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import { getAccessToken } from '@yawi/infrastructure';
import { useServices } from '@/app/providers';

interface CameraPlayerProps {
  indexCode: string;
  /** If true, auto-plays muted with no controls (for thumbnail previews) */
  compact?: boolean;
}

export function CameraPlayer({ indexCode, compact }: CameraPlayerProps) {
  const { cameraRepo } = useServices();
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let hls: Hls | undefined;
    let cancelled = false;

    async function initPlayer() {
      setLoading(true);
      setError(null);

      try {
        const streamUrl = await cameraRepo.getStreamUrl(indexCode);

        if (cancelled) return;

        if (!streamUrl) {
          setError('Stream no disponible');
          setLoading(false);
          return;
        }

        const video = videoRef.current;
        if (!video) return;

        const token = getAccessToken();

        if (Hls.isSupported()) {
          hls = new Hls({
            xhrSetup: (xhr) => {
              if (token) {
                xhr.setRequestHeader('Authorization', `Bearer ${token}`);
              }
            },
          });
          hls.loadSource(streamUrl);
          hls.attachMedia(video);
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            if (!cancelled) {
              setLoading(false);
              video.play().catch(() => {});
            }
          });
          hls.on(Hls.Events.ERROR, (_, data) => {
            if (data.fatal && !cancelled) {
              setError('Error de stream');
              setLoading(false);
            }
          });
          hlsRef.current = hls;
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
          // Safari native HLS
          video.src = streamUrl;
          video.addEventListener('loadedmetadata', () => {
            if (!cancelled) setLoading(false);
          });
          video.addEventListener('error', () => {
            if (!cancelled) {
              setError('Error de stream');
              setLoading(false);
            }
          });
        } else {
          setError('HLS no soportado');
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setError('Error al conectar');
          setLoading(false);
        }
      }
    }

    initPlayer();

    return () => {
      cancelled = true;
      hlsRef.current?.destroy();
      hlsRef.current = null;
    };
  }, [indexCode]);

  if (error) {
    return (
      <div className={`flex items-center justify-center bg-slate-900 text-slate-400 text-xs ${compact ? 'h-full' : 'h-48 rounded-lg'}`}>
        {error}
      </div>
    );
  }

  return (
    <div className={`relative bg-black ${compact ? '' : 'rounded-lg overflow-hidden'}`}>
      {loading && (
        <div className={`absolute inset-0 flex items-center justify-center bg-black/80 z-10 ${compact ? '' : 'rounded-lg'}`}>
          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      <video
        ref={videoRef}
        controls={!compact}
        muted
        playsInline
        className="w-full h-full object-cover"
        style={{ display: loading ? 'none' : 'block' }}
      />
    </div>
  );
}
